import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useDropdownStyles } from "../FDFilterDropdownStyles";
import { isNullOrUndefined } from "utils";
import { NEGATE_FILTER } from "Consts";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { SPECIAL_FIELD_TYPES } from "../FDFilterDropdownConsts";
import "styles/components/filterDropdown/fd_filter_dropdown.css";
import { SideAccordionItem } from "./SideFilterAccordion";
import { filterSortingExceptions, filterSortingById } from "settings/filterSortExceptions";

const SideFilterDropdown = ({
  FdType,
  Items = [],
  Name = "",
  Field = "",
  MaxSelectedItems = null,
  OnMaxSelection = null,
  SingleSelection = false, //will not trigger OnMaxSelection- only remove the prev selected, will not exclude as well- only include
  AllowExclude = true,
  ForceSelectedIndex = -1,
  ForceSelectedValue = null,
  isApply = false,
  setApply,
  openAccordions,
  setOpenAccordions,
  filterText,
  clearFilterText,
  filtersCount,
  GlobalTopFilterObject,
  GlobalActiveFilters,
  IsItemVisibleFunc,
  onRuleClick,
  allActiveFilters,
  setAllActiveFilters,
  OnClick = null,
}: any) => {
  const isFirstRender = useRef(true);
  const classes = useDropdownStyles();
  const dispatch = useDispatch();
  const [items, setItems] = useState(Items || []);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAllActiveFilters({ ...allActiveFilters, [Field]: activeFilters });

    if (Field === "hosts" || Field === "servername" || Field === "databasename") {
      const res = Object?.entries(activeFilters)
        .filter(([_, value]) => value === "on")
        .map(([key, _]) => key);

      const updated = { ...GlobalActiveFilters, [Field]: res };

      if (res?.length === 0) delete updated[Field];

      dispatch({
        type: globalFilterTypes.SET_ACTIVE_FILTERS,
        payload: updated,
      });
    }
  }, [activeFilters]);

  const getSelectedFilters = () => {
    const selectedFilterMode = GlobalTopFilterObject;
    const filterArr = selectedFilterMode[Field];

    if (
      SingleSelection &&
      (filterArr?.length > 1 ||
        (filterArr?.length === 1 && filterArr[0]?.startsWith(NEGATE_FILTER)))
    ) {
      return [];
    } else return selectedFilterMode[Field];
  };

  const currentFilterValue = getSelectedFilters();

  const onSingleSelectionClick = (selectedItem: any) => {
    const newFilterStatusList = { [selectedItem.name]: "on" };
    if (ForceSelectedIndex == -1 && activeFilters[selectedItem.name] == "on") {
      delete newFilterStatusList[selectedItem.name];
    }

    setActiveFilters(newFilterStatusList);
  };

  const publishGlobalFilter = useCallback(
    (values: any, list: any) => {
      const newFieldGlobalFilterValues = AllowExclude
        ? Object.entries(values).map(([listItem, value]) => {
            if (value === "off") {
              return `${NEGATE_FILTER}${listItem}`;
            }
            return listItem;
          })
        : Object.entries(values)
            .filter(([_, value]) => value !== "off")
            .map(([listItem, _]) => listItem);

      if (!AllowExclude) setActiveFilters({});

      dispatch({
        payload: {
          [Field]: newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : null,
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER,
      });

      dispatch({
        payload: {
          [Field]:
            newFieldGlobalFilterValues.length > 0
              ? newFieldGlobalFilterValues.map((name: string) => {
                  //@ts-ignore
                  const includesNegate = name.includes(NEGATE_FILTER);
                  const _name = includesNegate ? name?.replace(NEGATE_FILTER, "") : name;
                  const value = list?.find((a: any) => _name === a.name)?.value;
                  const id = list?.find((a: any) => _name === a.name)?.id;
                  return includesNegate ? `${NEGATE_FILTER}${id || value}` : `${id}` || value;
                })
              : null,
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
      });
    },
    [Field]
  );

  const isLimitReached = (currentChecked: any) => {
    if (MaxSelectedItems && currentChecked === undefined) {
      const excludedPlusIncludedItems = Object.values(activeFilters).filter((v) => v)?.length;

      if (excludedPlusIncludedItems >= MaxSelectedItems && OnMaxSelection) {
        //@ts-ignore
        OnMaxSelection();
      }
      return excludedPlusIncludedItems >= MaxSelectedItems;
    }
    return false;
  };

  useEffect(() => {
    if (Items) {
      const cleanItems = Items.filter(
        (option: any) => option.name !== undefined && option.name !== null
      );

      const mappedItems = cleanItems.map((f: any) => ({
        ...f,
        value: f?.value || f?.name,
      }));

      setItems(mappedItems);
    }
  }, [Items]);

  useEffect(() => {
    const map: any = {};
    if (currentFilterValue) {
      let valueOutOfRangeExists = false;
      for (const filterVal of currentFilterValue) {
        // prevent selected value where it's not opt the given filter list
        if (SPECIAL_FIELD_TYPES.includes(Field)) {
          if (filterVal?.startsWith(NEGATE_FILTER)) {
            map[filterVal.replace(NEGATE_FILTER, "")] = "off";
          } else {
            map[filterVal] = "on";
          }
        } else if (
          typeof filterVal === "string" &&
          items.some((item: any) => item.name === filterVal?.replace(NEGATE_FILTER, ""))
        ) {
          // Original handling for other fields
          if (filterVal?.startsWith(NEGATE_FILTER)) {
            map[filterVal.replace(NEGATE_FILTER, "")] = "off";
          } else {
            map[filterVal] = "on";
          }
        } else {
          valueOutOfRangeExists = true;
        }
      }
      setActiveFilters(map);

      if (
        items.length > 0 &&
        valueOutOfRangeExists &&
        Field !== "alertStatusType" &&
        Field !== "resourceName" &&
        Field !== "queryMemoryGrantRange"
      ) {
        publishGlobalFilter(map, items);
      }
    } else {
      resetSelected();
    }
  }, [currentFilterValue, JSON.stringify(items)]);

  const visibleItems = useMemo(() => {
    const result = IsItemVisibleFunc
      ? //@ts-ignore
        items.filter((item: any) => IsItemVisibleFunc(item.display ?? item.name))
      : items;

    const selected = result.filter((i: any) => GlobalTopFilterObject[Field]?.includes(i.name));
    const excluded = result.filter((i: any) =>
      GlobalTopFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
    );

    const nSelected = result
      .filter(
        (i: any) =>
          !GlobalTopFilterObject[Field]?.includes(i.name) &&
          !GlobalTopFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
      )
      .sort((a: any, b: any) => {
        // Keep [Unassigned] at the top
        if (a.name === "[Unassigned]") return -1;
        if (b.name === "[Unassigned]") return 1;
        // Don't sort weekdays - keep them in order received from api
        if (filterSortingExceptions?.includes(Field)) {
          return 0;
        }
        if (filterSortingById?.includes(Field)) {
          return a.id - b.id;
        }
        if (Field === "queryMemoryGrantRange") {
          return b.name - a.name;
        }
        // Sort everything else alphabetically
        return a?.name?.localeCompare(b?.name);
      });

    return [...selected, ...excluded, ...nSelected];
  }, [items, GlobalTopFilterObject, IsItemVisibleFunc]);

  const resetSelected = useCallback(() => {
    if (ForceSelectedIndex > -1 && items.length > 0) {
      const selectedItem: any = items[ForceSelectedIndex];
      if (selectedItem && selectedItem.name) {
        const activeFiltersCopy = { ...activeFilters };
        activeFiltersCopy[selectedItem.name] = "on";

        setActiveFilters(activeFiltersCopy);
      }
    } else if (!isNullOrUndefined(ForceSelectedValue) && items.length > 0) {
      const arrSelected = Array.isArray(ForceSelectedValue)
        ? ForceSelectedValue
        : [ForceSelectedValue];

      const filterListStatusCopy = SingleSelection ? {} : { ...activeFilters };
      arrSelected.forEach((selected) => {
        const selectedItemIndex = items.findIndex((category: any) => category.value == selected);
        if (selectedItemIndex > -1) {
          const selectedItem: any = items[selectedItemIndex];

          if (selectedItem && selectedItem.name) {
            filterListStatusCopy[selectedItem.name] = "on";
          }
        }
      });

      setActiveFilters(filterListStatusCopy);
    } else {
      if (FdType || Object.keys(GlobalTopFilterObject).length === 0) setActiveFilters({});
    }
  }, [
    ForceSelectedIndex,
    ForceSelectedValue,
    Name,
    JSON.stringify(activeFilters),
    JSON.stringify(items),
    GlobalTopFilterObject,
  ]);

  useEffect(() => {
    resetSelected();
  }, [ForceSelectedIndex]);

  useEffect(() => {
    resetSelected();
  }, [ForceSelectedValue]);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
      dispatch({
        type: globalFilterTypes.SET_ACTIVE_FILTERS,
        payload: null,
      });
    },
    []
  );

  const filterItemClick = (item: any) => {
    const currentChecked = activeFilters[item.name];
    let newChecked;

    if (AllowExclude) {
      if (currentChecked !== undefined) {
        if (currentChecked === "on") {
          newChecked = "off";
        } else if (currentChecked === "off" || currentChecked === "on") {
          newChecked = undefined;
        }
      } else {
        newChecked = "on";
      }
    }

    if (!AllowExclude) {
      newChecked = currentChecked === "on" ? undefined : "on";
    }

    if (SingleSelection) {
      onSingleSelectionClick(item);
      return;
    }

    // Special handling for allowExclude + maxSelectedItems=1
    if (MaxSelectedItems === 1 && AllowExclude && currentChecked === undefined) {
      const filterListStatusCopy: any = {};
      filterListStatusCopy[item.name] = "on";
      setActiveFilters(filterListStatusCopy);
      return;
    }

    if (isLimitReached(currentChecked)) {
      return;
    }

    const filterListStatusCopy = { ...activeFilters };

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }

    setActiveFilters(filterListStatusCopy);
  };

  useEffect(() => {
    if (isApply) {
      clearFilterText();
      publishGlobalFilter(activeFilters, items);
      setApply(false);
    }
  }, [isApply]);

  useEffect(() => {
    if (!OnClick) return;
    OnClick(Object?.keys(activeFilters));
  }, [activeFilters]);

  const dropdownOptions: any[] = visibleItems
    .filter((item: any) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    })
    .map((i) => ({
      ...i,
      label: i.name,
    }));

  const handleAccordionToggle = (optionName: any, isOpen: any) => {
    if (onRuleClick) {
      onRuleClick();
    } else {
      if (isOpen) {
        setOpenAccordions({ [optionName]: true });
      } else {
        setOpenAccordions((prev: any) => {
          const newOpenAccordions = { ...prev };
          delete newOpenAccordions[optionName];
          return newOpenAccordions;
        });
      }
    }
  };

  const singleOption = [{ label: Name, name: Name, field: Field, values: dropdownOptions }];

  return (
    <>
      <div className="euiFilterSelect__items" style={{ maxWidth: "400px", minWidth: "400px" }}>
        {singleOption?.map((option) => (
          <SideAccordionItem
            loading={isFirstRender.current ? loading : false}
            key={option.name || option.label}
            option={option}
            activeFilters={activeFilters}
            filterItemClick={filterItemClick}
            classes={classes}
            setActiveFilters={setActiveFilters}
            onToggle={handleAccordionToggle}
            isOpen={!!openAccordions[option.name]}
            filterText={filterText}
            filtersCount={filtersCount}
            allowExclude={AllowExclude}
          />
        ))}
        {singleOption.length === 0 && (
          <div style={{ padding: "16px", textAlign: "center" }}>No filters found</div>
        )}
      </div>
    </>
  );
};

export default SideFilterDropdown;
